import React, { useState, useCallback } from 'react'
import I18n from 'i18n-js'
import axios from 'axios'

import Button from "./Button"

const redirectTo = (url: string) => {
    console.log('redirect to', url)

    if (url !== '') {
        window.location.href = url
        return true
    }

    return false
}

const submitToNeo = (
    submitUrl: string,
    authenticityToken: string,
    firstname: string,
    lastname: string,
    adult: boolean,
    marketingOptIn: boolean,
    receiveBetRemindersEmail: boolean,
    receiveBetRemindersPush: boolean,
) => {
    return new Promise<void>((resolve, reject) => {
        const data = {
            authenticity_token: authenticityToken,
            firstname: firstname,
            lastname: lastname,
            adult: adult,
            marketing_opt_in: marketingOptIn,
            receive_bet_reminders_email: receiveBetRemindersEmail,
            receive_bet_reminders_push: receiveBetRemindersPush,
        }

        axios.post(submitUrl, data).then(({data}) => {
            if (data?.error) {
                reject(data.error)
            } else {
                resolve()
            }
        }).catch((err) => {
            reject(err)
        })
    })
}

interface SettingsFormProps {
    firstname: string;
    lastname: string;
    terms_url: string;
    submit_url: string;
    redirect_after_submit_url: string;
    authenticity_token: string;
    marketing_opt_in: boolean;
    receive_bet_reminders_email: boolean;
    receive_bet_reminders_push: boolean;
    adult: boolean;
}

const SettingsForm = (
    {
        firstname: defaultFirstname,
        lastname: defaultLastname,
        terms_url: termsUrl,
        submit_url: submitUrl,
        redirect_after_submit_url: redirectAfterSubmitUrl,
        authenticity_token: authenticityToken,
        marketing_opt_in: marketingOptIn,
        receive_bet_reminders_email: receiveBetRemindersEmail,
        receive_bet_reminders_push: receiveBetRemindersPush,
        adult: adult,
    }: SettingsFormProps
) => {
    const back = useCallback(() => {
        history.back();
    }, [])

    const [firstname, setFirstname] = useState(defaultFirstname || "")
    const [lastname, setLastname] = useState(defaultLastname || "")
    const [adultChecked, setAdultChecked] = useState<"18+" | "under_18">(adult ? "18+" : "under_18")

    const [marketingOptInChecked, setMarketingOptInChecked] = useState(marketingOptIn || false)
    const [receiveBetRemindersEmailChecked, setReceiveBetRemindersEmailChecked] = useState(receiveBetRemindersEmail || false)
    const [receiveBetRemindersPushChecked, setReceiveBetRemindersPushChecked] = useState(receiveBetRemindersPush || false)

    const [firstnameErrorMsg, setFirstnameErrorMsg] = useState<string | false>(false)
    const [lastnameErrorMsg, setLastnameErrorMsg] = useState<string | false>(false)
    const [submitting, setSubmitting] = useState(false)


    const submit = useCallback(() => {
        let earlyErrored = false

        if (!firstname) {
            setFirstnameErrorMsg(I18n.t('profiles.errors.firstname_missing'))
            earlyErrored = true
        } else {
            setFirstnameErrorMsg('')
        }

        if (!lastname) {
            setLastnameErrorMsg(I18n.t('profiles.errors.lastname_missing'))
            earlyErrored = true
        } else {
            setLastnameErrorMsg('')
        }

        if (!earlyErrored) {
            setSubmitting(true)
            submitToNeo(submitUrl,
                authenticityToken,
                firstname,
                lastname,
                adultChecked === "18+",
                marketingOptInChecked,
                receiveBetRemindersEmailChecked,
                receiveBetRemindersPushChecked,
            ).then(() => {
                if (!redirectTo(redirectAfterSubmitUrl)) {
                    // could not redirect?
                    setSubmitting(false)
                }
            })
                .catch((error) => {
                    switch (error.type) {
                        case "profile":
                            console.error(error.message)
                            break
                        case "firstname":
                            setFirstnameErrorMsg(error.message)
                            break
                        case "lastname":
                            setLastnameErrorMsg(error.message)
                            break
                    }
                    setSubmitting(false)
                })
        } else {
            console.log('early error -> don\'t submit anything')
        }
    }, [firstname, lastname, adultChecked, receiveBetRemindersEmailChecked, receiveBetRemindersPushChecked, marketingOptInChecked])

    const onChangeFirstname = useCallback((e) => {
        setFirstname(e.target.value)
    }, [])
    const onChangeLastname = useCallback((e) => {
        setLastname(e.target.value)
    }, [])
    const onAdultChange = useCallback((e) => {
        setAdultChecked(e.target.value)
    }, [])
    const onReceiveBetRemindersEmailChange = useCallback((e) => {
        setReceiveBetRemindersEmailChecked(e.target.checked)
    }, [])
    const onReceiveBetRemindersPushChange = useCallback((e) => {
        setReceiveBetRemindersPushChecked(e.target.checked)
    }, [])
    const onMarketingOptInChange = useCallback((e) => {
        setMarketingOptInChecked(e.target.checked)
    }, [])

    return (
        <div>
            <span className="completeProfile__notifications__back" onClick={submitting ? () => {} : back}>
                {I18n.t("complete_profile.notifications.back")}
            </span>
            <h2>
                {I18n.t("complete_profile.settings.title")}
            </h2>
            <p>
                <label className="completeProfile__inputLabel">
                    {I18n.t('complete_profile.firstname_label')} *
                    <input
                        type='text'
                        name='firstname'
                        id='firstname'
                        value={firstname}
                        onChange={onChangeFirstname}
                        autoFocus
                        maxLength={32}
                        className={'input' + (firstnameErrorMsg ? ' input--error' : '')}
                        placeholder={I18n.t('complete_profile.firstname_placeholder')}
                    />
                </label>
                {firstnameErrorMsg &&
                    <span className='form__error form__error--centered'>{firstnameErrorMsg}</span>}
            </p>
            <p>
                <label className="completeProfile__inputLabel">
                    {I18n.t("complete_profile.lastname_label")} *
                    <input
                        type='text'
                        name='lastname'
                        id='lastname'
                        value={lastname}
                        onChange={onChangeLastname}
                        autoFocus
                        maxLength={32}
                        className={'input' + (lastnameErrorMsg ? ' input--error' : '')}
                        placeholder={I18n.t('complete_profile.lastname_placeholder')}
                    />
                </label>
                {lastnameErrorMsg && (
                    <span className='form__error form__error--centered'>{lastnameErrorMsg}</span>
                )}
            </p>
            <p>
                <span dangerouslySetInnerHTML={{__html: I18n.t("complete_profile.adult")}}></span>
            </p>
            <p className='completeProfile__radioButton__container'>
                <label className="completeProfile__radioButton">
                    <input
                        type="radio"
                        name="adult"
                        value="18+"
                        defaultChecked={adult}
                        onChange={onAdultChange}
                    />
                    {I18n.t("complete_profile.adult_18plus")}
                </label>
                <label className="completeProfile__radioButton">
                    <input
                        type="radio"
                        name="adult"
                        value="under_18"
                        defaultChecked={!adult}
                        onChange={onAdultChange}
                    />
                    {I18n.t("complete_profile.adult_under18")}
                </label>
            </p>
            <h3 className="completeProfile__centeredHeading">
                {I18n.t("complete_profile.notifications.section_title")}
            </h3>
            <p className='completeProfile__checkbox__container'>
                <label className="completeProfile__checkbox__label">
                    <input
                        className='completeProfile__checkbox__input'
                        type='checkbox'
                        name='marketingOptIn'
                        defaultChecked={marketingOptInChecked}
                        onChange={onMarketingOptInChange}
                    />
                    <span className="completeProfile__checkbox__label__text" dangerouslySetInnerHTML={{__html: I18n.t('complete_profile.marketing')}} />
                </label>
            </p>
            <p className='completeProfile__checkbox__container'>
                <label className="completeProfile__checkbox__label">
                    <input
                        className='completeProfile__checkbox__input'
                        type='checkbox'
                        name='receiveBetRemindersEmail'
                        defaultChecked={receiveBetRemindersEmailChecked}
                        onChange={onReceiveBetRemindersEmailChange}
                    />
                    <span className="completeProfile__checkbox__label__text" dangerouslySetInnerHTML={{__html: I18n.t('complete_profile.receive_bet_reminders_email')}} />
                </label>
            </p>
            <p className='completeProfile__checkbox__container'>
                <label className="completeProfile__checkbox__label">
                    <input
                        className='completeProfile__checkbox__input'
                        type='checkbox'
                        name='receiveBetRemindersPush'
                        defaultChecked={receiveBetRemindersPushChecked}
                        onChange={onReceiveBetRemindersPushChange}
                    />
                    <span className="completeProfile__checkbox__label__text" dangerouslySetInnerHTML={{__html: I18n.t('complete_profile.receive_bet_reminders_push')}} />
                </label>
            </p>
            <p className='completeProfile__submitWrapper completeProfile__submitWrapper--withMargin'>
                <Button
                    primary
                    disabled={submitting}
                    onClick={submit}
                >
                    {I18n.t('complete_profile.save')}
                </Button>
            </p>
        </div>
    )
}

export default SettingsForm
